import {Injectable} from '@angular/core';
import {RequesterService} from "../shared/services/requester/requester.service";
import {DbResponse} from "../shared/interceptors/db-response";
import {BehaviorSubject} from "rxjs";
import {OptimizeProgressMapper} from "./website/image-optimizer/optimizer-main/optimize-progress-mapper";
import {ImageOptimizerReportDataMapper} from "./website/image-optimizer/optimizer-main/image-optimizer-report-data-mapper";
import {Config} from "../config/config";

import * as fromStore from '../store';
import {Store} from "@ngrx/store";
import {DbStoreState} from '../store';
import {clientRoles} from "../user/user-roles-actions";
import {ISubSiteData} from "../components/common/create-subsite/models/sub-site-data.model";
import {TypeUtils} from "../shared/utils/type-utils";
import {IOPagesData} from "./website/booster/mappers/io-list";
import mockData from "../../assets/json/mock-data.json";
import {CookieService} from "ngx-cookie";
import {map} from "rxjs/operators";

export type IOMode = 'lossless' | 'lossy40' | 'lossy' | null;

@Injectable()
export class WebsiteService {

  private currentWorkspaceId: any;
  private ownerWorkspaceId: any;
  private currentWorkspaceRoleId: any;

  constructor(
    private _requester: RequesterService,
    private _store: Store<DbStoreState>,
    private _cookieService: CookieService
  ) {
    this._store.select(fromStore.getWorkspaceIdEntities).subscribe( workspaceIdEntities => {
      this.currentWorkspaceRoleId = workspaceIdEntities.currentWorkspaceRoleId;
      this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
      this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
    });
  }

  public getTroubleshootingData(websiteId: number) {
    return this._requester.performance
      .post( `workspaces/${this.currentWorkspaceId}/domains/${websiteId}/check-domain`, null, null);
    /*let httpRequest: HttpRequest<any> = new HttpRequest('GET', `${websiteUrl}/?rest_route=/tenweb_so/v1/check_domain`);
    httpRequest = httpRequest.clone({
      method: 'POST'
    });
    return this.http.request<DbResponse>(httpRequest);*/
  }

  public getWebsites(updateCache = true): BehaviorSubject<DbResponse> {
    return this._requester.core.get(`workspaces/${this.currentWorkspaceId}/domains`, null, updateCache);
  }

  public getAiBuilderDemoWebsite(): BehaviorSubject<DbResponse> {
    if (!this.currentWorkspaceId) {
      const response: any = mockData['getPageLink'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/get_page_link`, null, true);
  }

  public getVPZoneCreatedResponse(): BehaviorSubject<DbResponse> {
    return this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/get_client_instances`);
  }

  public getWebsite(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.core.get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}`, null, true);
  }

  public getWhatsNew(): BehaviorSubject<DbResponse> {
    return this._requester.core.get(`workspaces/${this.currentWorkspaceId}/release_notes`, null, true);
  }

  public getWhatsNewIsClient(): BehaviorSubject<DbResponse> {
    return this._requester.core.get(`workspaces/${this.currentWorkspaceId}/release_notes/last`, null, true);
  }

  public getWpToken(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/single`, null, true)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = response.data.token;
        }

        return response;
      })));
  }


  public getWhiteLabel(): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/domains/white-label`, null, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public storeWhiteLabel(data: any, domainIds: any, cacheIcon: any, useDefaultIcon: boolean, allDomains: boolean): BehaviorSubject<DbResponse> {
    const whiteLabelData = {
      company_name: data.companyName || '10Web',
      cache_text: 'We use NGINX FastCGI Static Page Caching to cache everything from pages to feeds to 301-redirects on sub-domains. It helps your site to load super fast for your non-logged-in visitors.',
      domain_ids: JSON.stringify(domainIds),
      default_icon: useDefaultIcon ? 1 : 0,
      all_domains: allDomains ? 1 : 0
    };

    if (useDefaultIcon) {
      Object.assign(whiteLabelData, {
        cache_icon_big: ""
      });
    }

    if (cacheIcon) {
      Object.assign(whiteLabelData, {
        cache_icon_big: cacheIcon
      });
    }

    return this._requester.core
      .post( `workspaces/${this.currentWorkspaceId}/domains/white-label/store`, null, whiteLabelData);
  }

  public removeWebsite(websiteId: number, password: any): BehaviorSubject<DbResponse> {

    const passwordData = {
      password: password
    };
    return this._requester.core
      .delete(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/delete`, null, passwordData);
  }

  public removeDemoWebsite(): BehaviorSubject<DbResponse> {
    return this._requester.core
      .delete(`workspaces/${this.currentWorkspaceId}/client/demo-site/delete`, null, null);
  }

  public getImageOptimizerReport(websiteId: number, page: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.optimizer
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/report?page=${page}&rows=20`, null, true)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = response.data.map(ImageOptimizerReportDataMapper.map);
        }

        return response;
      })));
  }

  public getImageOptimizerPagesMediaReport(websiteId: number, updateCache = false): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.optimizer
      .get(`compress/workspaces/${this.currentWorkspaceId}/domains/${websiteId}/report`, null, updateCache)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          const bulkData = response.data.bulk.map(data => new IOPagesData(data));
          const pagesData = response.data.pages.map(data => new IOPagesData(data));
          response.data = Object.assign({}, {bulkData: bulkData}, {pagesData: pagesData});
        }
        return response;
      })));
  }

  public optimize(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.optimizer
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/optimize`, null, true)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = response.data.token;
        }

        return response;
      })));
  }

  public checkOptimizeProgress(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.optimizer
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/progress `, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success) {
          response.data = OptimizeProgressMapper.map(response.data);
        }

        return response;
      })));
  }

  public saveSettings(data: IOMode): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.optimizer
      .post(`workspaces/${this.currentWorkspaceId}/settings`, null, data)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = response.data.token;
        }

        return response;
      })));
  }

  public setIOMode(websiteId: number, mode: IOMode, webP: number = 1): BehaviorSubject<DbResponse> {
    const data = {};
    data['level'] = !TypeUtils.isUndefined(mode) && mode ? mode : null;
    data['convert_webp'] = !TypeUtils.isUndefined(webP) ? webP : 0;

    return <BehaviorSubject<DbResponse>>(this._requester.optimizer
      .post(`settings/workspaces/${this.currentWorkspaceId}/domains/${websiteId}/store`, null, data)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = response.data.token;
        }
        return response;
      })));
  }

  public getIOMode(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['get'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.optimizer
      .get(`settings/workspaces/${this.currentWorkspaceId}/domains/${websiteId}/get`, null)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public ioOptimize(websiteId: number, pageUrl: any, force: number = 0): BehaviorSubject<DbResponse> {
    const data = {
      page_url: pageUrl
    };
    data['force'] = !TypeUtils.isUndefined(force) ? force : 0;
    return this._requester.optimizer
      .post(`compress/workspaces/${this.currentWorkspaceId}/domains/${websiteId}/optimize-custom-page`, null, data);
  }

  public autoinstall(data: any): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/domains/autoinstall`, null, data);
  }

  public coreUpdate(domain_id: any): BehaviorSubject<DbResponse> {
    return this._requester.manager
      .post(`workspaces/${this.currentWorkspaceId}/domains/${domain_id}/wp-core-update`, null, null);
  }

  public getOverviewWidgetOrder(websiteId: any): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['ordering'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/widgets/ordering`, null, true);
  }

  public setOverviewWidgetOrder(data: any): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/domains/widgets/ordering`, null, data);
  }

  public checkConnectionAvailability(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/availability`);
  }

  public autoLogin(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/autologin`);
  }

  public enableWebsiteDevEnvironment(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/staging/enable`);
  }

  /* ADD domain tag to domains*/
  public addDomainTag(json: any): BehaviorSubject<DbResponse> {
    const data = {
      data: JSON.stringify(json)
    };
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/website-tags`, null, data);
  }

  /* ADD domain tag*/
  public addUserTag(tagTitle: string): BehaviorSubject<DbResponse> {
    const data = {
      tag_title: tagTitle
    };
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/tags/add`, null, data);
  }

  /* EDIT domain tag*/
  public editUserTag(tagTitle: string, tagId: number): BehaviorSubject<DbResponse> {
    const data = {
      tag_title: tagTitle
    };
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/tags/${tagId}/edit`, null, data);
  }

  /* EDIT domain tag*/
  public removeUserTag(tagId: number): BehaviorSubject<DbResponse> {
    return this._requester.core
      .delete(`workspaces/${this.currentWorkspaceId}/user/tags/${tagId}/delete`);
  }

  public getUserTags(): BehaviorSubject<DbResponse> {
    let currentWorkspaceId = this.currentWorkspaceId;
    if ([clientRoles.SITE_DEV, clientRoles.SUITE_DEV].includes(this.currentWorkspaceRoleId)) {
      currentWorkspaceId = this.ownerWorkspaceId;
    }
    return this._requester.core
      .get(`workspaces/${currentWorkspaceId}/user/tags/list`, null, true);
  }

  /*TODO 10webx Run move to new service file*/
  public runTenWebX(websiteId: number, data: any): BehaviorSubject<DbResponse> {
    const url = data.websiteUrl.split("\n").length > 1 ? JSON.stringify(data.websiteUrl.split("\n")) : data.websiteUrl;
    const _data = {
      domain_id: websiteId,
      url: url,
      magic_input: data.magicInput,
      language: navigator.language,
      layout_map: data.layoutMap ? 1 : 0
    };
    return this._requester.aiAssistant
      .post(`templates/workspaces/${this.currentWorkspaceId}/create-from-url`, null, _data);
  }
  public runAiBuilder(websiteId: number, data: any, pageTitle?: string, postStatus?: string, kitData?: any, onboarding?: boolean, menuItemPosition?, menuItemId?, menuTermId?): BehaviorSubject<DbResponse> {
    const url = JSON.stringify(Object.values(data));
    let _data = {
      domain_id: websiteId,
      url: url,
      magic_input: '',
      language: navigator.language,
      layout_map: 0
    };

    if (pageTitle) {
      _data = Object.assign({}, _data, {
        page_title: pageTitle
      });
    }

    if (onboarding) {
      _data = Object.assign({}, _data, {
        onboarding: true
      });
    }

    if (kitData) {
      _data = Object.assign({}, _data, {
        kit_data: kitData
      });
    }

    if (postStatus) {
      _data = Object.assign({}, _data, {
        post_status: postStatus
      });
    }

    if (menuItemPosition) {
      _data = Object.assign({}, _data, {
        menu_item_position: menuItemPosition,
        menu_term_id: menuTermId,
        menu_item_id: menuItemId,
        post_status: 'publish'
      });
    }

    return this._requester.aiAssistant
      .post(`templates/workspaces/${this.currentWorkspaceId}/create-from-url`, null, _data);
  }
  public checkUrl(url: any): BehaviorSubject<DbResponse> {
    const _data = {
      url: url,
    };
    return this._requester.aiAssistant
      .post(`templates/workspaces/${this.currentWorkspaceId}/check-page`, null, _data);
  }

  public getGoogleInsightScore(websiteUrl, strategy): BehaviorSubject<DbResponse> {
    const data = {
      url: websiteUrl,
      key: Config.googleInsightsKey
    };
    return this._requester.google
      .get(`workspaces/${this.currentWorkspaceId}/pagespeedonline/v5/runPagespeed?strategy=${strategy}`, data, null);
  }

  public checkWebsiteSpeedFromCore(newWebsiteId, oldWebsiteId): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/domains/${newWebsiteId}/initial/${oldWebsiteId}/score`, null, null);
  }

  public createSubWebsite(data: Partial<ISubSiteData>, websiteId: number): BehaviorSubject<DbResponse> {

    return <BehaviorSubject<DbResponse>>(this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/add-new-blog`, null, data)
    );
  }

  public removeSubDomain(websiteId, childId): BehaviorSubject<DbResponse> {

    return <BehaviorSubject<DbResponse>>(this._requester.core
        .delete(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/delete-blog/${childId}`, null, null)
        .pipe(map((response: DbResponse): DbResponse => {

          return response;
        })));
  }

  public createBlankPage(websiteId: number, pageTitle: string, postStatus: string, menuItemPosition?, menuItemId?, menuTermId?): BehaviorSubject<DbResponse> {
    let _data = {
      page_title: pageTitle,
      post_status: postStatus,
    };
    if (menuItemPosition) {
      _data = Object.assign({}, _data, {
        menu_item_position: menuItemPosition,
        menu_term_id: menuTermId,
        menu_item_id: menuItemId,
        post_status: 'publish'
      });
    }
    return this._requester.aiAssistant
      .post(`builder/workspaces/${this.currentWorkspaceId}/domains/${websiteId}/pages/blank/add`, null, _data);
  }

  public setFrontPage(websiteId: number, pageId: number): BehaviorSubject<DbResponse> {
    const _data = {
      page_id: pageId,
    };
    return this._requester.aiAssistant
      .post(`builder/workspaces/${this.currentWorkspaceId}/domains/${websiteId}/pages/front/set`, null, _data);
  }

  public removePage(websiteId: number, pageId: number): BehaviorSubject<DbResponse> {
    const _data = {
      page_ids: JSON.stringify([pageId])
    };
    return this._requester.aiAssistant
      .post(`builder/workspaces/${this.currentWorkspaceId}/domains/${websiteId}/pages/delete`, null, _data);
  }

  public publishPage(websiteId: number, pageId: number, action: string): BehaviorSubject<DbResponse> {
    const _data = {
      action: action,
      page_ids: JSON.stringify([pageId])
    };
    return this._requester.aiAssistant
      .post(`builder/workspaces/${this.currentWorkspaceId}/domains/${websiteId}/pages/publish`, null, _data);
  }

  public recreateWithAiAssistant(websiteId: number, params: Record<string, string | number | boolean>, retry: boolean = false): BehaviorSubject<DbResponse> {
    const getExperimentalCookie = this._cookieService.get('db-experiment');
    const templateSelectionMode = getExperimentalCookie ? JSON.parse(getExperimentalCookie) && JSON.parse(getExperimentalCookie).templateSelectionMode && JSON.parse(getExperimentalCookie).templateSelectionMode : 'usability';
    params = {...params, no_stock: true, post_status: 'publish', tone: 'formal', template_selection: templateSelectionMode};
    let data = {params};
    if (websiteId) {
      data = Object.assign(data, {domain_id: websiteId});
    }
    else {
      data.params = {...params, ai_type: 'ai_builder_demo'};
    }
    if (retry) {
      data = Object.assign(data, {retry: true});
    }
    return this._requester.aiAssistant
      .post(`templates/workspaces/${this.currentWorkspaceId}/create-from-input`, null, data);
  }
}
